import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEcomV1PickupLocation from './ecom-v1-pickup-location-pickup-locations.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/** PickupLocation is the main entity of PickupLocations that can be used for lorem ipsum dolor */
export interface PickupLocation {
  /**
   * PickupLocation ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this PickupLocation was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this PickupLocation was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Pickup Location Name
   * @minLength 1
   * @maxLength 100
   */
  name?: string | null;
  /** Pickup Location Address */
  address?: Address;
  /**
   * Expected delivery time in free text. For example, `"3-5 business days"`.
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /**
   * Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.
   * @maxLength 1000
   */
  instructions?: string | null;
  /** inactive pickup locations should not be shown in checkout */
  active?: boolean | null;
  /**
   * at runtime for a given pickup input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /**
   * This pickup location is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   */
  deliveryRegionIds?: string[];
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** @maxLength 150 */
  addressLine1?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually a state, region, prefecture, or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** @maxLength 100 */
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** @maxLength 150 */
  addressLine?: string | null;
}

export interface StreetAddress {
  number?: string;
  name?: string;
}

export interface ConditionalRates {
  /**
   * there is an AND logic between all the conditions. Empty conditions means true.
   * The amount of the rate that will be returned if all conditions are met.
   * @decimalValue options { gte:0, lte:999999999, maxScale:3 }
   */
  amount?: string;
  /**
   * For example: weight > 0 and weight <= 10
   * @maxSize 10
   */
  conditions?: Condition[];
  /** When this flag is set to true, multiply the amount by the number of line items passed on the request. */
  multiplyByQuantity?: boolean;
}

export interface Condition {
  type?: ConditionTypeWithLiterals;
  /**
   * The value in respective to the condition type
   * Weight values should be in the same weight units of the store: KG / LB
   * Total price is according to the store currency
   * Quantity of items should be integers
   * @decimalValue options { gte:0, lte:999999999, maxScale:6 }
   */
  value?: string;
  /** Logical operator. */
  operator?: LogicalOperatorWithLiterals;
}

export enum ConditionType {
  BY_TOTAL_WEIGHT = 'BY_TOTAL_WEIGHT',
  BY_TOTAL_PRICE = 'BY_TOTAL_PRICE',
  BY_TOTAL_QUANTITY = 'BY_TOTAL_QUANTITY',
}

/** @enumType */
export type ConditionTypeWithLiterals =
  | ConditionType
  | 'BY_TOTAL_WEIGHT'
  | 'BY_TOTAL_PRICE'
  | 'BY_TOTAL_QUANTITY';

export enum LogicalOperator {
  EQ = 'EQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
}

/** @enumType */
export type LogicalOperatorWithLiterals =
  | LogicalOperator
  | 'EQ'
  | 'GT'
  | 'GTE'
  | 'LT'
  | 'LTE';

export interface DiffmatokyPayload {
  left?: string;
  right?: string;
  compareChannel?: string;
  entityId?: string;
  errorInformation?: ErrorInformation;
  tags?: string[];
}

export interface ErrorInformation {
  stackTrace?: string;
}

export interface CreatePickupLocationRequest {
  /** PickupLocation to be created */
  pickupLocation: PickupLocation;
}

export interface CreatePickupLocationResponse {
  /** The created PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface GetPickupLocationRequest {
  /**
   * Id of the PickupLocation to retrieve
   * @format GUID
   */
  pickupLocationId: string;
}

export interface GetPickupLocationResponse {
  /** The retrieved PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface UpdatePickupLocationRequest {
  /** PickupLocation to be updated, may be partial */
  pickupLocation: PickupLocation;
}

export interface UpdatePickupLocationResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface DeletePickupLocationRequest {
  /**
   * Id of the PickupLocation to delete
   * @format GUID
   */
  pickupLocationId: string;
}

export interface DeletePickupLocationResponse {}

export interface QueryPickupLocationRequest {
  /** WQL expression */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryPickupLocationResponse {
  /** The retrieved PickupLocations */
  pickupLocations?: PickupLocation[];
  /** Paging metadata */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface AddDeliveryRegionRequest {
  /**
   * Id of the PickupLocation to add to the delivery region
   * @format GUID
   */
  pickupLocationId: string;
  /**
   * Id of the DeliveryRegion to add the PickupLocation to
   * @format GUID
   */
  deliveryRegionId: string;
  /** Revision of the PickupLocation */
  revision: string | null;
}

export interface AddDeliveryRegionResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface RemoveDeliveryRegionRequest {
  /**
   * Id of the PickupLocation to add to the delivery region
   * @format GUID
   */
  pickupLocationId: string;
  /**
   * Id of the DeliveryRegion to add the PickupLocation to
   * @format GUID
   */
  deliveryRegionId: string;
  /** Revision of the PickupLocation */
  revision: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** The updated PickupLocation */
  pickupLocation?: PickupLocation;
}

export interface BulkCreatePickupLocationRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  pickupLocations: PickupLocation[];
}

export interface BulkCreatePickupLocationResponse {
  /** @maxSize 100 */
  pickupLocations?: PickupLocation[];
  errors?: PickupLocationError[];
}

export interface PickupLocationError {
  _id?: string;
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdatePickupLocationRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  pickupLocations: PickupLocation[];
}

export interface BulkUpdatePickupLocationResponse {
  /** @maxSize 100 */
  pickupLocations?: PickupLocation[];
  errors?: PickupLocationError[];
}

export interface BulkDeletePickupLocationRequest {
  /**
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  pickupLocationIds: string[];
}

export interface BulkDeletePickupLocationResponse {
  errors?: PickupLocationError[];
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreatePickupLocationApplicationErrors =
  | {
      code?: 'LOCATIONS_IN_SHIPPING_RULE_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LOCATIONS_IN_DELIVERY_REGIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LIMIT_OF_OPTIONS_PER_DELIVERY_REGION_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkCreatePickupLocationApplicationErrors =
  | {
      code?: 'LOCATIONS_IN_SHIPPING_RULE_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LOCATIONS_IN_DELIVERY_REGIONS_LIMIT_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'LIMIT_OF_OPTIONS_PER_DELIVERY_REGION_EXCEEDED';
      description?: string;
      data?: Record<string, any>;
    };

/**
 * Creates a new PickupLocation
 * @param pickupLocation - PickupLocation to be created
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocation
 * @requiredField pickupLocation.address
 * @permissionId ECOM.PICKUP_LOCATION_CREATE
 * @applicableIdentity APP
 * @returns The created PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.CreatePickupLocation
 */
export async function createPickupLocation(
  pickupLocation: NonNullablePaths<PickupLocation, `address`, 2>
): Promise<
  NonNullablePaths<
    PickupLocation,
    | `address.streetAddress.number`
    | `address.streetAddress.name`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    4
  > & {
    __applicationErrorsType?: CreatePickupLocationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocation: pickupLocation }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocation.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.createPickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocation: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocation']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Get a PickupLocation by id
 * @param pickupLocationId - Id of the PickupLocation to retrieve
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_READ
 * @applicableIdentity APP
 * @returns The retrieved PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.GetPickupLocation
 */
export async function getPickupLocation(
  pickupLocationId: string
): Promise<
  NonNullablePaths<
    PickupLocation,
    | `address.streetAddress.number`
    | `address.streetAddress.name`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
  });

  const reqOpts = ambassadorWixEcomV1PickupLocation.getPickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update a PickupLocation
 * Delivery regions cannot be updated using this method, use AddDeliveryRegion and RemoveDeliveryRegion instead.
 * @param _id - PickupLocation ID
 * @public
 * @documentationMaturity preview
 * @requiredField _id
 * @requiredField pickupLocation
 * @requiredField pickupLocation.revision
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @applicableIdentity APP
 * @returns The updated PickupLocation
 * @fqn wix.ecom.v1.PickupLocations.UpdatePickupLocation
 */
export async function updatePickupLocation(
  _id: string,
  pickupLocation: NonNullablePaths<UpdatePickupLocation, `revision`, 2>
): Promise<
  NonNullablePaths<
    PickupLocation,
    | `address.streetAddress.number`
    | `address.streetAddress.name`
    | `rates`
    | `rates.${number}.amount`
    | `rates.${number}.multiplyByQuantity`
    | `deliveryRegionIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      pickupLocation: { ...pickupLocation, id: _id },
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocation.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.updatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )?.pickupLocation!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { pickupLocation: '$[1]' },
        explicitPathsToArguments: { 'pickupLocation.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'pickupLocation']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdatePickupLocation {
  /**
   * PickupLocation ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Represents the current state of an item. Each time the item is modified, its `revision` changes. for an update operation to succeed, you MUST pass the latest revision
   * @readonly
   */
  revision?: string | null;
  /**
   * Represents the time this PickupLocation was created
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Represents the time this PickupLocation was last updated
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Pickup Location Name
   * @minLength 1
   * @maxLength 100
   */
  name?: string | null;
  /** Pickup Location Address */
  address?: Address;
  /**
   * Expected delivery time in free text. For example, `"3-5 business days"`.
   * @maxLength 500
   */
  deliveryTime?: string | null;
  /**
   * Instructions for carrier. For example, `"Please knock on the door. If unanswered, please call contact number. Thanks."`.
   * @maxLength 1000
   */
  instructions?: string | null;
  /** inactive pickup locations should not be shown in checkout */
  active?: boolean | null;
  /**
   * at runtime for a given pickup input, up to one rate (price) should be returned in an option. If more than one rate is valid then we return the lowest one.
   * @maxSize 50
   */
  rates?: ConditionalRates[];
  /**
   * This pickup location is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   */
  deliveryRegionIds?: string[];
}

/**
 * Delete a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to delete
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_DELETE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.DeletePickupLocation
 */
export async function deletePickupLocation(
  pickupLocationId: string
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.deletePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Query PickupLocations using [WQL - Wix Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
 * @public
 * @documentationMaturity preview
 * @permissionId ECOM.PICKUP_LOCATION_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.QueryPickupLocation
 */
export function queryPickupLocation(): PickupLocationsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    PickupLocation,
    'CURSOR',
    QueryPickupLocationRequest,
    QueryPickupLocationResponse
  >({
    func: async (payload: QueryPickupLocationRequest) => {
      const reqOpts =
        ambassadorWixEcomV1PickupLocation.queryPickupLocation(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryPickupLocationRequest['query']) => {
      const args = [query, {}] as [QueryPickupLocationRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({
      data,
    }: HttpResponse<QueryPickupLocationResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [{ path: 'pickupLocations.address' }],
          },
        ])
      );

      return {
        items: transformedData?.pickupLocations,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface PickupLocationsQueryResult extends QueryCursorResult {
  items: PickupLocation[];
  query: PickupLocationsQueryBuilder;
  next: () => Promise<PickupLocationsQueryResult>;
  prev: () => Promise<PickupLocationsQueryResult>;
}

export interface PickupLocationsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  eq: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ne: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  ge: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  gt: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  le: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   * @documentationMaturity preview
   */
  lt: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   * @documentationMaturity preview
   */
  startsWith: (
    propertyName:
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city',
    value: string
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasSome: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any[]
  ) => PickupLocationsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   * @documentationMaturity preview
   */
  hasAll: (
    propertyName: 'deliveryRegionIds',
    value: any[]
  ) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  in: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: any
  ) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  exists: (
    propertyName:
      | '_createdDate'
      | '_updatedDate'
      | 'name'
      | 'address.country'
      | 'address.subdivision'
      | 'address.city'
      | 'deliveryRegionIds',
    value: boolean
  ) => PickupLocationsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object.
   * @documentationMaturity preview
   */
  limit: (limit: number) => PickupLocationsQueryBuilder;
  /** @param cursor - A pointer to specific record
   * @documentationMaturity preview
   */
  skipTo: (cursor: string) => PickupLocationsQueryBuilder;
  /** @documentationMaturity preview */
  find: () => Promise<PickupLocationsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.ecom.v1.PickupLocations.QueryPickupLocation
 * @requiredField query
 */
export async function typedQueryPickupLocation(
  query: PickupLocationQuery
): Promise<
  NonNullablePaths<
    QueryPickupLocationResponse,
    | `pickupLocations`
    | `pickupLocations.${number}.address.streetAddress.number`
    | `pickupLocations.${number}.address.streetAddress.name`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.queryPickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocations.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface PickupLocationQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['deliveryRegionIds'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: [
        '_createdDate',
        '_updatedDate',
        'address.city',
        'address.country',
        'address.subdivision',
        'name'
      ];
      operators: '*';
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  PickupLocation,
  PickupLocationQuerySpec
>;
export type PickupLocationQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).  
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<
      PickupLocation,
      PickupLocationQuerySpec,
      PickupLocationQuery
    >(),
  },
};

/**
 * Add a DeliveryRegion to a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to add to the delivery region
 * @param deliveryRegionId - Id of the DeliveryRegion to add the PickupLocation to
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.AddDeliveryRegion
 */
export async function addDeliveryRegion(
  pickupLocationId: string,
  deliveryRegionId: string,
  options: NonNullablePaths<AddDeliveryRegionOptions, `revision`, 2>
): Promise<
  NonNullablePaths<
    AddDeliveryRegionResponse,
    | `pickupLocation.address.streetAddress.number`
    | `pickupLocation.address.streetAddress.name`
    | `pickupLocation.rates`
    | `pickupLocation.rates.${number}.amount`
    | `pickupLocation.rates.${number}.multiplyByQuantity`
    | `pickupLocation.deliveryRegionIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts = ambassadorWixEcomV1PickupLocation.addDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pickupLocationId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AddDeliveryRegionOptions {
  /** Revision of the PickupLocation */
  revision: string | null;
}

/**
 * Remove a DeliveryRegion from a PickupLocation
 * @param pickupLocationId - Id of the PickupLocation to add to the delivery region
 * @param deliveryRegionId - Id of the DeliveryRegion to add the PickupLocation to
 * @public
 * @documentationMaturity preview
 * @requiredField deliveryRegionId
 * @requiredField options
 * @requiredField options.revision
 * @requiredField pickupLocationId
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.RemoveDeliveryRegion
 */
export async function removeDeliveryRegion(
  pickupLocationId: string,
  deliveryRegionId: string,
  options: NonNullablePaths<RemoveDeliveryRegionOptions, `revision`, 2>
): Promise<
  NonNullablePaths<
    RemoveDeliveryRegionResponse,
    | `pickupLocation.address.streetAddress.number`
    | `pickupLocation.address.streetAddress.name`
    | `pickupLocation.rates`
    | `pickupLocation.rates.${number}.amount`
    | `pickupLocation.rates.${number}.multiplyByQuantity`
    | `pickupLocation.deliveryRegionIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationId: pickupLocationId,
    deliveryRegionId: deliveryRegionId,
    revision: options?.revision,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.removeDeliveryRegion(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocation.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          pickupLocationId: '$[0]',
          deliveryRegionId: '$[1]',
          revision: '$[2].revision',
        },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationId', 'deliveryRegionId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RemoveDeliveryRegionOptions {
  /** Revision of the PickupLocation */
  revision: string | null;
}

/**
 * Bulk Create for new PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocations
 * @requiredField pickupLocations.address
 * @permissionId ECOM.PICKUP_LOCATION_CREATE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.BulkCreatePickupLocation
 */
export async function bulkCreatePickupLocation(
  pickupLocations: NonNullablePaths<PickupLocation, `address`, 2>[]
): Promise<
  NonNullablePaths<
    BulkCreatePickupLocationResponse,
    | `pickupLocations`
    | `pickupLocations.${number}.address.streetAddress.number`
    | `pickupLocations.${number}.address.streetAddress.name`
    | `errors`
    | `errors.${number}._id`
    | `errors.${number}.error.code`
    | `errors.${number}.error.description`,
    6
  > & {
    __applicationErrorsType?: BulkCreatePickupLocationApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocations: pickupLocations }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocations.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkCreatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocations.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocations: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocations']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Update a PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocations
 * @requiredField pickupLocations._id
 * @requiredField pickupLocations.revision
 * @permissionId ECOM.PICKUP_LOCATION_UPDATE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.BulkUpdatePickupLocation
 */
export async function bulkUpdatePickupLocation(
  pickupLocations: NonNullablePaths<PickupLocation, `_id` | `revision`, 2>[]
): Promise<
  NonNullablePaths<
    BulkUpdatePickupLocationResponse,
    | `pickupLocations`
    | `pickupLocations.${number}.address.streetAddress.number`
    | `pickupLocations.${number}.address.streetAddress.name`
    | `errors`
    | `errors.${number}._id`
    | `errors.${number}.error.code`
    | `errors.${number}.error.description`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ pickupLocations: pickupLocations }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [{ path: 'pickupLocations.address' }],
      },
    ]
  );

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkUpdatePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'pickupLocations.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocations: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocations']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Delete a PickupLocation
 * @public
 * @documentationMaturity preview
 * @requiredField pickupLocationIds
 * @permissionId ECOM.PICKUP_LOCATION_DELETE
 * @applicableIdentity APP
 * @fqn wix.ecom.v1.PickupLocations.BulkDeletePickupLocation
 */
export async function bulkDeletePickupLocation(
  pickupLocationIds: string[]
): Promise<
  NonNullablePaths<
    BulkDeletePickupLocationResponse,
    | `errors`
    | `errors.${number}._id`
    | `errors.${number}.error.code`
    | `errors.${number}.error.description`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    pickupLocationIds: pickupLocationIds,
  });

  const reqOpts =
    ambassadorWixEcomV1PickupLocation.bulkDeletePickupLocation(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { pickupLocationIds: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['pickupLocationIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
